import React, { useState, useEffect } from 'react';
import { 
  Scale, ShieldAlert, AlertTriangle, CheckCircle, RefreshCw, 
  Search, ExternalLink, Download, FileText, ArrowLeft,
  Calendar, BookOpen, Send, Sparkles, Filter, ChevronDown, ChevronUp,
  Building, Landmark, Coins, FileCheck, Layers, Loader2
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { ClickableReportViewer } from './ClickableReportViewer';

interface LegislationTrackerProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface LegislationUpdate {
  id: string;
  category: 'tax' | 'procurement' | 'court' | 'construction' | 'dstu' | string;
  title: string;
  act_name: string;
  effective_date: string | null;
  summary: string;
  impact_analysis: string | null;
  action_recommendations: string | null;
  risk_level: 'high' | 'medium' | 'low' | string;
  source_url: string | null;
  created_at: string;
}

const CATEGORIES = [
  { id: 'all', labelUk: 'Усі сфери', labelEn: 'All Categories', labelPl: 'Wszystkie dziedziny', icon: Layers },
  { id: 'tax', labelUk: 'Податки & ДПС (ПКУ / KSeF)', labelEn: 'Tax & Revenue', labelPl: 'Podatki (KSeF / VAT / CIT)', icon: Coins },
  { id: 'budget', labelUk: 'Бюджет & ДКСУ (КЕКВ / БКУ)', labelEn: 'Public Budget & Treasury', labelPl: 'Finanse publiczne & Budżet', icon: Landmark },
  { id: 'procurement', labelUk: 'Закупівлі (Prozorro / TED)', labelEn: 'Procurement & TED', labelPl: 'Zamówienia publiczne (Pzp / TED)', icon: Landmark },
  { id: 'court', labelUk: 'Судова практика (ВС / NSA)', labelEn: 'Court Practice', labelPl: 'Orzecznictwo sądowe (SN / NSA)', icon: Scale },
  { id: 'construction', labelUk: 'Будівництво & FIDIC', labelEn: 'Construction & FIDIC', labelPl: 'Budownictwo (Prawo budowlane & FIDIC)', icon: Building },
  { id: 'dstu', labelUk: 'ДСТУ & Комплаєнс (RODO)', labelEn: 'Standards & GDPR', labelPl: 'Standardy & RODO / Compliance', icon: FileCheck },
];

export default function LegislationTracker({
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: LegislationTrackerProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [updates, setUpdates] = useState<LegislationUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [scanJurisdiction, setScanJurisdiction] = useState<'UA' | 'PL' | 'EU'>('UA');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestSuccess, setDigestSuccess] = useState(false);

  const fetchUpdates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedRisk !== 'all') params.append('riskLevel', selectedRisk);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const res = await fetch(`/api/legislation-updates?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setUpdates(data.updates || []);
      }
    } catch (err) {
      console.error('Error fetching legislation updates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, [selectedCategory, selectedRisk]);

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const res = await fetch('/api/legislation-updates/scan-now', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: selectedCategory, jurisdiction: scanJurisdiction })
      });
      if (res.ok) {
        const data = await res.json();
        setUpdates(data.updates || []);
        alert(isUk ? `ШІ успішно виявив та заніс ${data.addedCount} свіжих оновлень законодавства з клікабельними лінками!` : `AI detected and recorded ${data.addedCount} fresh legislative updates with clickable links!`);
      } else {
        const errData = await res.json();
        alert(errData.error || (isUk ? 'Помилка сканування' : 'Scan error'));
      }
    } catch (err: any) {
      console.error('Scan error:', err);
      alert(err.message || (isUk ? 'Помилка підключення' : 'Connection error'));
    } finally {
      setScanning(false);
    }
  };

  const handleSendDigestTelegram = async () => {
    setSendingDigest(true);
    try {
      const res = await fetch('/api/legislation-updates/send-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        setDigestSuccess(true);
        setTimeout(() => setDigestSuccess(false), 3000);
      } else {
        const data = await res.json();
        alert(data.error || 'Помилка відправки дайджесту');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка надсилання в Telegram');
    } finally {
      setSendingDigest(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getFullDigestMarkdown = () => {
    let md = `# 🏛️ ЗВЕДЕНИЙ ДАЙДЖЕСТ ЗМІН ЗАКОНОДАВСТВА ТА БЮДЖЕТНОГО ОБЛІКУ\n\n`;
    md += `*Згенеровано аналітичною системою Deep Research Hub (${new Date().toLocaleDateString('uk-UA')})*\n\n---\n\n`;
    updates.forEach((u, i) => {
      md += `## ${i + 1}. ${u.title}\n\n`;
      md += `- **Нормативний акт:** ${u.act_name}\n`;
      md += `- **Дата набрання чинності:** ${u.effective_date || '2026'}\n`;
      md += `- **Рівень ризику:** ${u.risk_level === 'high' ? '🔴 Високий' : u.risk_level === 'medium' ? '🟡 Середній' : '🟢 Інформаційний'}\n\n`;
      md += `### Зміст змін:\n${u.summary}\n\n`;
      if (u.impact_analysis) md += `### Вплив на підприємство / установу:\n${u.impact_analysis}\n\n`;
      if (u.action_recommendations) md += `### Рекомендовані дії:\n${u.action_recommendations}\n\n`;
      if (u.source_url) md += `🔗 [Офіційне першоджерело на zakon.rada.gov.ua](${u.source_url})\n\n---\n\n`;
    });
    return md;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-16 animate-fadeIn">
      {/* Top Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={onGoHome || onBack}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700 cursor-pointer"
              title={isUk ? "Повернутися назад" : "Go Back"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 bg-indigo-500/10 border border-indigo-500/30 rounded-xl flex items-center justify-center text-indigo-400 shadow-inner">
              <Scale className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  {isUk ? "ШІ-Трекер Змін Законодавства" : "Legal & Tax Legislation Auto-Watcher"}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                  <span>{isUk ? "ШІ-Моніторинг 24/7" : "24/7 AI Watcher"}</span>
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                {isUk 
                  ? "Автоматичний аналіз оновлень ПКУ, Бюджетного кодексу, КЕКВ (Інструкція № 333), Prozorro, практики АМКУ, Верховного Суду та ДСТУ з клікабельними лінками" 
                  : "Continuous AI monitoring of Tax Code, Budget Code, KEKV, Public Procurement, AMKU appeals, Supreme Court & DSTU regulations with clickable links"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Scan Jurisdiction Selector */}
            <div className="flex items-center bg-slate-950 p-0.5 rounded-xl border border-slate-700 text-[10px] font-mono font-bold">
              <button
                type="button"
                onClick={() => setScanJurisdiction('UA')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  scanJurisdiction === 'UA' ? 'bg-amber-500 text-slate-950 font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
                title="Україна (ПКУ, БКУ, КЕКВ, ДСТУ, Prozorro)"
              >
                🇺🇦 UA
              </button>
              <button
                type="button"
                onClick={() => setScanJurisdiction('PL')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  scanJurisdiction === 'PL' ? 'bg-amber-500 text-slate-950 font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
                title="Polska (Kodeks cywilny, KSeF, VAT, CIT, Pzp)"
              >
                🇵🇱 PL
              </button>
              <button
                type="button"
                onClick={() => setScanJurisdiction('EU')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  scanJurisdiction === 'EU' ? 'bg-amber-500 text-slate-950 font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
                title="European Union (Directives, FIDIC, GDPR, TED)"
              >
                🇪🇺 EU
              </button>
            </div>

            <button
              type="button"
              onClick={handleScanNow}
              disabled={scanning}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 shadow-lg shadow-indigo-600/20 cursor-pointer disabled:opacity-50"
            >
              {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>{scanning ? (isPl ? "Skanowanie..." : isUk ? "Сканування..." : "Scanning...") : (isPl ? "⚡ Skanuj zmiany (AI)" : isUk ? "⚡ Сканувати зміни (ШІ)" : "⚡ AI Scan Now")}</span>
            </button>

            <button
              type="button"
              onClick={handleSendDigestTelegram}
              disabled={sendingDigest}
              className="px-3.5 py-2 bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs uppercase rounded-xl transition-all flex items-center gap-1.5 shadow-md shadow-sky-600/20 cursor-pointer disabled:opacity-50"
            >
              {sendingDigest ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : digestSuccess ? <CheckCircle className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
              <span>{digestSuccess ? (isPl ? "Wysłano!" : isUk ? "Надіслано!" : "Sent!") : (isPl ? "Alert do TG" : isUk ? "Дайджест у TG" : "TG Digest")}</span>
            </button>

            <TelegramExportButton
              title="Дайджест законодавчих змін"
              content={getFullDigestMarkdown()}
              agentName="ШІ-Трекер Законодавства"
              agentId="legislation-tracker"
              isUk={isUk}
              variant="emerald"
            />

            <ExcelExportButton
              title="Таблиця законодавчих змін"
              content={getFullDigestMarkdown()}
              fileName="legislation_updates_digest"
              isUk={isUk}
              variant="primary"
            />
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-3">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer select-none ${
                  isSelected
                    ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20"
                    : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 border border-slate-700/50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{isPl ? cat.labelPl : isUk ? cat.labelUk : cat.labelEn}</span>
              </button>
            );
          })}
        </div>

        {/* Risk & Search Row */}
        <div className="flex flex-col sm:flex-row items-center gap-3 pt-2 border-t border-slate-800/80">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={selectedRisk}
              onChange={(e) => setSelectedRisk(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-xs text-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-amber-400 cursor-pointer"
            >
              <option value="all">{isUk ? "Усі рівні ризику" : "All Risk Levels"}</option>
              <option value="high">🔴 {isUk ? "Високий ризик (терміново)" : "High Risk"}</option>
              <option value="medium">🟡 {isUk ? "Середній ризик" : "Medium Risk"}</option>
              <option value="low">🟢 {isUk ? "Інформаційний" : "Low / Info"}</option>
            </select>
          </div>

          <div className="relative flex-1 w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchUpdates()}
              placeholder={isUk ? "Пошук за законом, наказом (КЕКВ, ПКУ, БКУ) або змістом..." : "Search legal acts or keywords..."}
              className="w-full pl-9 pr-4 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-400"
            />
          </div>

          <button
            type="button"
            onClick={fetchUpdates}
            className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-lg border border-slate-700 transition-colors cursor-pointer"
          >
            {isUk ? "Шукати" : "Search"}
          </button>
        </div>
      </div>

      {/* Updates List */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
          <p className="text-xs text-slate-400 mt-2 font-mono">{isUk ? "Завантаження законодавчих оновлень..." : "Loading updates..."}</p>
        </div>
      ) : updates.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500 space-y-3">
          <Scale className="w-12 h-12 mx-auto opacity-30 text-amber-400" />
          <h3 className="text-sm font-bold text-slate-300">{isUk ? "Оновлень за вашим запитом не знайдено" : "No updates found"}</h3>
          <p className="text-xs max-w-md mx-auto">{isUk ? "Натисніть кнопку «⚡ Сканувати зміни (ШІ)» для оновлення реєстру." : "Click 'AI Scan Now' to update regulations."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((item) => {
            const isExpanded = !!expandedIds[item.id];
            const isHigh = item.risk_level === 'high';
            const isMedium = item.risk_level === 'medium';

            const riskBorder = isHigh ? 'border-red-500/40 hover:border-red-500/70' : isMedium ? 'border-amber-500/40 hover:border-amber-500/70' : 'border-slate-800 hover:border-slate-700';

            return (
              <div 
                key={item.id} 
                className={`bg-slate-900/90 border ${riskBorder} rounded-2xl p-5 space-y-3 shadow-lg transition-all`}
              >
                {/* Card Header Row */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Risk Badge */}
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1 ${
                      isHigh 
                        ? 'bg-rose-950/60 text-rose-300 border border-rose-700/60' 
                        : isMedium 
                        ? 'bg-amber-950/60 text-amber-300 border border-amber-700/60' 
                        : 'bg-emerald-950/60 text-emerald-300 border border-emerald-700/60'
                    }`}>
                      {isHigh ? '🔴 Високий ризик' : isMedium ? '🟡 Середній ризик' : '🟢 Інформаційний'}
                    </span>

                    {/* Category */}
                    <span className="px-2 py-0.5 bg-slate-800 text-slate-300 rounded text-[10px] font-mono uppercase border border-slate-700">
                      {item.category.toUpperCase()}
                    </span>

                    {/* Date */}
                    {item.effective_date && (
                      <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-amber-400" />
                        <span>Чинний з: <strong>{item.effective_date}</strong></span>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {item.source_url && (
                      <a 
                        href={item.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 rounded-lg text-xs text-sky-400 hover:text-sky-300 font-semibold flex items-center gap-1 transition-colors"
                      >
                        <span>Офіційний текст на zakon.rada.gov.ua</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Title & Act Name */}
                <div>
                  <h3 className="text-base font-black text-white tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-xs font-mono text-amber-400 mt-0.5">
                    📜 {item.act_name}
                  </p>
                </div>

                {/* Summary with Clickable Links */}
                <div className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  <ClickableReportViewer content={item.summary} />
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="pt-3 border-t border-slate-800/80 space-y-3 animate-fadeIn">
                    {item.impact_analysis && (
                      <div className="p-3.5 bg-slate-950/80 border border-amber-500/20 rounded-xl space-y-1">
                        <h4 className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                          <span>Вплив на підприємство / установу та фінансові ризики:</span>
                        </h4>
                        <div className="text-xs text-slate-300 leading-relaxed">
                          <ClickableReportViewer content={item.impact_analysis} />
                        </div>
                      </div>
                    )}

                    {item.action_recommendations && (
                      <div className="p-3.5 bg-slate-950/80 border border-emerald-500/20 rounded-xl space-y-1">
                        <h4 className="text-xs font-bold text-emerald-300 flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Рекомендований алгоритм дій:</span>
                        </h4>
                        <div className="text-xs text-slate-300 leading-relaxed">
                          <ClickableReportViewer content={item.action_recommendations} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Card Action Bar */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-800/60">
                  <button
                    type="button"
                    onClick={() => toggleExpand(item.id)}
                    className="text-xs text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <span>{isExpanded ? (isUk ? "Згорнути деталі" : "Hide Details") : (isUk ? "Розгорнути аналіз та рекомендації" : "Show Impact & Recommendations")}</span>
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>

                  <div className="flex items-center gap-2">
                    <TelegramExportButton
                      title={item.title}
                      content={`# ${item.title}\n\n**Акт:** ${item.act_name}\n**Дата:** ${item.effective_date || '2026'}\n\n## Зміст:\n${item.summary}\n\n## Вплив на бізнес:\n${item.impact_analysis || ''}\n\n## Рекомендації:\n${item.action_recommendations || ''}\n\n🔗 Джерело: ${item.source_url || 'https://zakon.rada.gov.ua'}`}
                      agentName="Юридичний Трекер"
                      agentId="legislation-tracker"
                      isUk={isUk}
                      variant="primary"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
